import React, { useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import ReactModal from "react-modal";
import {
  registerModalOpen,
  registerModalClose,
} from "../../../utils/modalUtils";
import styles from "./Modal.module.css";

const Modal = (props) => {
  const { isOpen = true, children } = props;
  const modalRef = useRef(null);

  // Focus trap function
  const handleKeyDown = useCallback((event) => {
    if (!modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.key === "Tab") {
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
  }, []);

  // Focus management and modal registration
  useEffect(() => {
    if (isOpen) {
      registerModalOpen();

      if (modalRef.current) {
        // Focus the first focusable element in the modal
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }

      // Add event listener for focus trap
      document.addEventListener("keydown", handleKeyDown);
    } else {
      registerModalClose();
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (isOpen) {
        registerModalClose();
      }
    };
  }, [isOpen, handleKeyDown]);

  return (
    <ReactModal
      className={styles.modal}
      overlayClassName={styles.overlay}
      shouldCloseOnOverlayClick={props.handleClose}
      isOpen={isOpen}
      onAfterOpen={() => {
        // Ensure focus is trapped within the modal
        if (modalRef.current) {
          const focusableElements = modalRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (focusableElements.length > 0) {
            focusableElements[0].focus();
          }
        }
      }}
    >
      <div ref={modalRef} onKeyDown={handleKeyDown}>
        {children}
      </div>
    </ReactModal>
  );
};

Modal.propTypes = {
  children: PropTypes.node,
  isOpen: PropTypes.bool,
  handleClose: PropTypes.func,
};

export default Modal;
